/**
 * Non-working-day policy (docs/plans/planned-task-schedule.md §3.8).
 *
 * Non-working = outside the tenant working week ∪ the line's resolved holiday
 * calendar. Applied AFTER pattern expansion and exception application.
 *
 * Pinned semantics:
 * - 'keep' output is independent of workWeek and holidays.
 * - 'nearest' ties resolve to the EARLIER working day.
 * - A shifted date past ends.onDate is dropped.
 * - A shift colliding with an existing occurrence dedupes.
 */

import { ScheduleNonWorkingDayPolicy, ScheduleWeekday } from './schedule.rule';
import { addDays, isoWeekday } from './schedule.compile';

/** How far a shift will chase a working day before giving up (a full fortnight of closures). */
const MAX_SHIFT_DAYS = 14;

export interface NonWorkingDayCalendar {
    workWeek: readonly ScheduleWeekday[];
    holidays?: readonly string[];
}

export function isWorkingDay(date: string, calendar: NonWorkingDayCalendar): boolean {

    if (calendar.holidays?.includes(date)) {
        return false;
    }

    return calendar.workWeek.includes(isoWeekday(date));
}

/** First working day at or after `date`, or undefined when none is found within the chase window. */
function shiftForward(date: string, calendar: NonWorkingDayCalendar): string | undefined {

    let cursor = date;

    for (let step = 0; step <= MAX_SHIFT_DAYS; step += 1) {
        if (isWorkingDay(cursor, calendar)) {
            return cursor;
        }

        cursor = addDays(cursor, 1);
    }

    return undefined;
}

/** First working day at or before `date`, or undefined when none is found within the chase window. */
function shiftBackward(date: string, calendar: NonWorkingDayCalendar): string | undefined {

    let cursor = date;

    for (let step = 0; step <= MAX_SHIFT_DAYS; step += 1) {
        if (isWorkingDay(cursor, calendar)) {
            return cursor;
        }

        cursor = addDays(cursor, -1);
    }

    return undefined;
}

/** Distance in whole days between two local dates (positive when `to` is later). */
function dayDistance(from: string, to: string): number {

    let distance = 0;
    let cursor = from;

    while (cursor < to && distance <= MAX_SHIFT_DAYS * 2) {
        cursor = addDays(cursor, 1);
        distance += 1;
    }

    return distance;
}

/** Nearest working day; an equidistant tie resolves to the EARLIER day (pinned). */
function shiftNearest(date: string, calendar: NonWorkingDayCalendar): string | undefined {

    const earlier = shiftBackward(date, calendar);
    const later = shiftForward(date, calendar);

    if (!earlier) {
        return later;
    }

    if (!later) {
        return earlier;
    }

    return dayDistance(earlier, date) <= dayDistance(date, later) ? earlier : later;
}

/** Resolve one occurrence under the policy; undefined means the occurrence is dropped. */
export function applyPolicyToDate(date: string, policy: ScheduleNonWorkingDayPolicy, calendar: NonWorkingDayCalendar): string | undefined {

    if (policy === 'keep') {
        return date;
    }

    if (isWorkingDay(date, calendar)) {
        return date;
    }

    switch (policy) {
        case 'skip':
            return undefined;
        case 'next':
            return shiftForward(date, calendar);
        case 'previous':
            return shiftBackward(date, calendar);
        case 'nearest':
            return shiftNearest(date, calendar);
    }
}

/**
 * Apply the policy across a set of occurrences: shifts run per date, results
 * are deduped (shift collisions) and re-sorted, and anything shifted past
 * `endsOnDate` is dropped.
 */
export function applyNonWorkingDayPolicy(dates: readonly string[], policy: ScheduleNonWorkingDayPolicy, calendar: NonWorkingDayCalendar, endsOnDate?: string): string[] {

    if (policy === 'keep') {
        return Array.from(new Set(dates)).sort();
    }

    const resolved = new Set<string>();

    for (const date of dates) {
        const shifted = applyPolicyToDate(date, policy, calendar);

        if (!shifted) {
            continue;
        }

        if (endsOnDate && shifted > endsOnDate) {
            continue;
        }

        resolved.add(shifted);
    }

    return Array.from(resolved).sort();
}
